import { NavLink } from "react-router-dom";
import clsx from "clsx";
import { LayoutDashboard, GraduationCap, CalendarDays, Users, UserRound, ScrollText, type LucideIcon } from "lucide-react";
import type { AuthUser } from "../../types";

interface NavItem {
  to: string;
  label: string;
  icon: LucideIcon;
  roles: AuthUser["role"][];
}

const NAV_ITEMS: NavItem[] = [
  { to: "/dashboard", label: "Overview", icon: LayoutDashboard, roles: ["SUPER_ADMIN", "EVENT_MANAGER", "EVENT_STAFF"] },
  { to: "/courses", label: "Courses", icon: GraduationCap, roles: ["SUPER_ADMIN"] },
  { to: "/events", label: "Events", icon: CalendarDays, roles: ["SUPER_ADMIN", "EVENT_MANAGER", "EVENT_STAFF"] },
  { to: "/users", label: "Users", icon: Users, roles: ["SUPER_ADMIN"] },
  { to: "/parents", label: "Parents & Students", icon: UserRound, roles: ["SUPER_ADMIN"] },
  { to: "/audit-log", label: "Audit Log", icon: ScrollText, roles: ["SUPER_ADMIN"] },
];

export function Sidebar({ role, open }: { role: AuthUser["role"]; open: boolean }): JSX.Element {
  const items = NAV_ITEMS.filter((item) => item.roles.includes(role));

  return (
    <aside
      className={clsx(
        "fixed inset-y-0 left-0 z-40 w-64 transform border-r border-slate-200 bg-white transition-transform duration-200 dark:border-slate-700 dark:bg-slate-800 lg:translate-x-0",
        open ? "translate-x-0" : "-translate-x-full",
      )}
    >
      <div className="flex h-16 items-center gap-2.5 border-b border-slate-200 px-6 dark:border-slate-700">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg bg-brand-gradient text-sm font-bold text-white shadow-glow">
          E
        </span>
        <span className="text-lg font-bold tracking-tight text-slate-900 dark:text-slate-100">Event Management</span>
      </div>
      <nav className="flex flex-col gap-1 p-3">
        {items.map((item) => {
          const Icon = item.icon;
          return (
            <NavLink
              key={item.to}
              to={item.to}
              className={({ isActive }) =>
                clsx(
                  "relative flex items-center gap-2.5 rounded-lg px-3 py-2 text-sm font-medium transition-all duration-150",
                  isActive
                    ? "bg-gradient-to-r from-brand-50 to-transparent text-brand-700 dark:from-brand-900/40 dark:to-transparent dark:text-brand-300"
                    : "text-slate-600 hover:translate-x-0.5 hover:bg-slate-100 dark:text-slate-400 dark:hover:bg-slate-700 dark:hover:text-slate-200",
                )
              }
            >
              {({ isActive }) => (
                <>
                  {isActive && (
                    <span className="absolute inset-y-1 left-0 w-1 rounded-full bg-brand-gradient" aria-hidden="true" />
                  )}
                  <Icon className="h-[18px] w-[18px] shrink-0" strokeWidth={2} aria-hidden="true" />
                  {item.label}
                </>
              )}
            </NavLink>
          );
        })}
      </nav>
    </aside>
  );
}
